
import React, { useEffect, useMemo, useState } from "react";
import {
  FunnelIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

const EMPTY_FILTERS = {
  postId: "",
  status: "",
  postSearch: "",
  commentSearch: "",
};

const CommentSearchModal = ({
  isOpen,
  onClose,
  posts = [],
  filters = EMPTY_FILTERS,
  onApply,
}) => {
  const [formData, setFormData] = useState(EMPTY_FILTERS);

  useEffect(() => {
    if (isOpen) {
      setFormData({
        postId: filters.postId || "",
        status: filters.status || "",
        postSearch: filters.postSearch || "",
        commentSearch: filters.commentSearch || "",
      });
    }
  }, [
    isOpen,
    filters.postId,
    filters.status,
    filters.postSearch,
    filters.commentSearch,
  ]);

  const filteredPosts = useMemo(() => {
    const keyword = formData.postSearch.trim().toLowerCase();

    if (!keyword) {
      return posts || [];
    }

    return (posts || []).filter((post) =>
      String(post.title || "")
        .toLowerCase()
        .includes(keyword)
    );
  }, [posts, formData.postSearch]);

  if (!isOpen) {
    return null;
  }

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((currentFormData) => ({
      ...currentFormData,
      [name]: value,
    }));
  };

  const handleReset = () => {
    setFormData(EMPTY_FILTERS);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    onApply({
      ...formData,
      page: 1,
      limit: 1000,
    });
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-4xl overflow-hidden rounded-xl bg-base-100 shadow-2xl">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <div>
            <h2 className="flex items-center gap-2 text-xl font-bold">
              <FunnelIcon className="h-6 w-6 text-blue-600" />
              Tìm kiếm bình luận nâng cao
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              Lọc sâu theo bài viết, nội dung bình luận và trạng thái.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="btn btn-ghost btn-sm"
            title="Đóng"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="max-h-[70vh] space-y-5 overflow-y-auto px-6 py-5">
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
              <div>
                <label className="mb-1 block text-sm font-medium">
                  Tìm tên bài viết
                </label>
                <input
                  type="text"
                  name="postSearch"
                  value={formData.postSearch}
                  onChange={handleChange}
                  placeholder="Nhập một phần tiêu đề bài viết"
                  className="input input-bordered w-full"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Danh sách bài viết bên dưới sẽ được lọc theo nội dung này.
                </p>
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium">
                  Tìm nội dung bình luận
                </label>
                <input
                  type="text"
                  name="commentSearch"
                  value={formData.commentSearch}
                  onChange={handleChange}
                  placeholder="Nhập nội dung cần tìm trong bình luận"
                  className="input input-bordered w-full"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
              <div>
                <label className="mb-1 block text-sm font-medium">
                  Chọn chính xác bài viết
                </label>
                <select
                  name="postId"
                  value={formData.postId}
                  onChange={handleChange}
                  className="select select-bordered w-full"
                >
                  <option value="">Tất cả bài viết</option>
                  {filteredPosts.map((post) => (
                    <option key={post.id} value={post.id}>
                      {post.title}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  Có {filteredPosts.length} bài viết phù hợp.
                </p>
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium">
                  Trạng thái bình luận
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="select select-bordered w-full"
                >
                  <option value="">Tất cả trạng thái</option>
                  <option value="pending">Chờ duyệt</option>
                  <option value="approved">Đã duyệt</option>
                  <option value="rejected">Từ chối</option>
                </select>
              </div>
            </div>
          </div>

          <div className="flex flex-col-reverse gap-3 border-t bg-base-200 px-6 py-4 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={handleReset}
              className="btn btn-outline"
            >
              Đặt lại
            </button>
            <button
              type="button"
              onClick={onClose}
              className="btn btn-ghost"
            >
              Đóng
            </button>
            <button type="submit" className="btn btn-primary">
              <MagnifyingGlassIcon className="h-5 w-5" />
              Áp dụng bộ lọc
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CommentSearchModal;

